import { useCallback, useEffect, useState } from 'react';
import type { PhaseId, PhaseStatus } from '@wedpo/soc2';

/** localStorage-backed state. The kit is a static demo: no backend yet. */
export function useLocalStorage<T>(key: string, initial: T) {
  const storageKey = `wedpo.soc2.${key}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [storageKey, value]);
  return [value, setValue] as const;
}

export type ViewId =
  | 'info'
  | 'journey'
  | 'sysdesc'
  | 'policies'
  | 'evidence'
  | PhaseId;

export interface PhaseDef {
  id: PhaseId;
  n: string;
  navLabel: string;
  title: string;
  desc: string;
  status: PhaseStatus;
}

export const PHASES: PhaseDef[] = [
  {
    id: 'p0',
    n: '0',
    navLabel: 'Foundations',
    title: 'Foundations - what you need to know',
    desc: 'Scoping decisions: report type, TSC, observation window, system boundary, RACI, initial risk assessment.',
    status: 'done',
  },
  {
    id: 'p1',
    n: '1',
    navLabel: 'Onboarding & kickoff',
    title: 'Onboarding & kickoff',
    desc: 'Onboarding form, kickoff deck, preliminary checklist, and scoped access for your auditor.',
    status: 'active',
  },
  {
    id: 'p2',
    n: '2',
    navLabel: 'Gap analysis',
    title: 'Gap analysis',
    desc: 'Assess each control against current state, each mapped to its owning stakeholder.',
    status: 'locked',
  },
  {
    id: 'p3',
    n: '3',
    navLabel: 'Plan & evidence',
    title: 'Plan & evidence',
    desc: 'Auto-generated remediation plan from gaps, plus the evidence repository with Type-2 collection cadence.',
    status: 'locked',
  },
  {
    id: 'p4',
    n: '4',
    navLabel: 'Implementation',
    title: 'Implementation',
    desc: 'Put the plan into practice - customize and adopt policies, write SOPs, and stand up the remaining technical and documentation requirements.',
    status: 'locked',
  },
  {
    id: 'p5',
    n: '5',
    navLabel: 'Internal audit',
    title: 'Internal audit',
    desc: 'Readiness review - test operating effectiveness over the window and log exceptions.',
    status: 'locked',
  },
  {
    id: 'p6',
    n: '6',
    navLabel: 'External audit',
    title: 'External audit & report',
    desc: 'Hand off to the CPA firm, track fieldwork, and receive the SOC 2 report.',
    status: 'locked',
  },
];

/* ---------------------------------------------------------------------------
 * Mock-style presentation model. The product is shown as the six activity
 * phases from the toolkit mock (Assess … Finalize). Internally the app still
 * uses the p0..p6 views + data; these six phases are a folding/relabel layer
 * over them, so nothing in the shared @wedpo/soc2 package changes.
 * ------------------------------------------------------------------------- */
export type MockPhaseId =
  | 'assess'
  | 'plan'
  | 'implement'
  | 'evidence'
  | 'auditor'
  | 'finalize';

export interface MockPhase {
  id: MockPhaseId;
  num: string; // '01'..'06'
  name: string; // Assess, Plan, …
  tag: string; // short subtitle
  desc: string;
  views: ViewId[]; // underlying live views this phase folds in
  primary: ViewId; // the view the nav item / stepper node opens
}

export const MOCK_PHASES: MockPhase[] = [
  {
    id: 'assess',
    num: '01',
    name: 'Assess',
    tag: 'Gap analysis',
    desc: 'Scope, kick off, and rate every control across the trust criteria.',
    views: ['p0', 'p1', 'p2'],
    primary: 'p2',
  },
  {
    id: 'plan',
    num: '02',
    name: 'Plan',
    tag: 'Remediation roadmap',
    desc: 'Turn every gap into an owned, scheduled remediation task.',
    views: ['p3'],
    primary: 'p3',
  },
  {
    id: 'implement',
    num: '03',
    name: 'Implement',
    tag: 'Policies & controls',
    desc: 'Adopt the templates and drive every control to ready.',
    views: ['p4'],
    primary: 'p4',
  },
  {
    id: 'evidence',
    num: '04',
    name: 'Evidence',
    tag: 'Collect & feedback',
    desc: 'Upload proof for each control and track coverage.',
    views: ['evidence'],
    primary: 'evidence',
  },
  {
    id: 'auditor',
    num: '05',
    name: 'Auditor',
    tag: 'Access & review',
    desc: 'Readiness review, operating-effectiveness testing, and the PBC list.',
    views: ['p5'],
    primary: 'p5',
  },
  {
    id: 'finalize',
    num: '06',
    name: 'Finalize',
    tag: 'Reports & letters',
    desc: 'Auditor fieldwork, the letters of record, and the final SOC 2 report.',
    views: ['p6'],
    primary: 'p6',
  },
];

/** Which mock phase a given view belongs to (for active-nav highlighting). */
export function mockPhaseForView(v: ViewId): MockPhaseId | null {
  const p = MOCK_PHASES.find((m) => m.views.includes(v));
  return p ? p.id : null;
}

/** Roll the p0..p6 statuses up into the six mock phases. */
export function computeMockPhaseStatus(
  ps: Record<PhaseId, PhaseStatus>,
): Record<MockPhaseId, PhaseStatus> {
  const roll = (ids: PhaseId[]): PhaseStatus => {
    const s = ids.map((i) => ps[i]);
    if (s.every((x) => x === 'done')) return 'done';
    if (s.some((x) => x === 'active' || x === 'done')) return 'active';
    return 'locked';
  };
  return {
    assess: roll(['p0', 'p1', 'p2']),
    plan: ps.p3,
    implement: ps.p4,
    // Evidence has no p-view; it opens once gap analysis is done and is
    // "done" only when implementation is complete.
    evidence:
      ps.p2 === 'done' ? (ps.p4 === 'done' ? 'done' : 'active') : 'locked',
    auditor: ps.p5,
    finalize: ps.p6,
  };
}

/**
 * Editor mode lets the owner preview every section, including the gated
 * phases the public sees as locked. Toggle with `?edit=1` in the URL
 * (sticks via localStorage); clear with `?edit=0`. The static demo has no
 * auth yet, so this is a lightweight, link-based gate, not a security boundary.
 */
export function useEditorMode(): boolean {
  const [editor] = useState<boolean>(() => {
    const key = 'wedpo.soc2.editor';
    try {
      const flag = new URLSearchParams(window.location.search).get('edit');
      if (flag === '1') {
        localStorage.setItem(key, '1');
        return true;
      }
      if (flag === '0') {
        localStorage.removeItem(key);
        return false;
      }
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  return editor;
}

/**
 * Marketing gate: the views a public visitor can open freely. Anything else
 * (the paid phases) opens the kit proposal form. This is independent of the
 * progressive unlock below, which governs the customer / editor experience.
 */
export const PUBLIC_FREE: ReadonlySet<ViewId> = new Set<ViewId>([
  'info',
  'journey',
  'sysdesc',
  'policies',
  'p0',
]);

/**
 * Progressive unlock. Phase 0 is always done; finishing Phase 1 (onboarding +
 * pre-work) flips Phase 2 from locked to active, and completing the Phase 2 gap
 * questionnaire flips Phase 3 from locked to active. Later phases stay locked
 * until their own work lands. This drives nav lock icons, the Journey map, and
 * the "done" markers - the realistic flow a customer walks.
 */
export function computePhaseStatus(
  p1Done: boolean,
  p2Done = false,
): Record<PhaseId, PhaseStatus> {
  return {
    p0: 'done',
    p1: p1Done ? 'done' : 'active',
    p2: p1Done ? (p2Done ? 'done' : 'active') : 'locked',
    p3: p1Done && p2Done ? 'active' : 'locked',
    p4: 'locked',
    p5: 'locked',
    p6: 'locked',
  };
}

/** Smooth scroll to top whenever the view changes. */
export function useScrollTopOnChange(dep: unknown) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dep]);
}

export function useToggleSet<T>(initial: T[]) {
  const [set, setSet] = useState<Set<T>>(new Set(initial));
  const toggle = useCallback((v: T) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }, []);
  return [set, toggle] as const;
}
